import { ApolloClient, HttpLink, InMemoryCache } from "apollo-boost"
import { persistCache } from "apollo-cache-persist"
import { ApolloLink } from "apollo-link"
import { setContext } from "apollo-link-context"
import { onError } from "apollo-link-error"
import { Maybe } from "fgr-lib-business-logic/lib/common/types"
import { Router as RouterProps } from "next/router"
import { ResponseErrorType } from "../../../graphql/generated-types"
import logger from "../../../server/logger"
import { envVars } from "../../../utils/config"
import { isBrowser } from "../../../utils/general"
import { getRouterQuery } from "../../../utils/router"
import { getTableOrderingId } from "../../../utils/url"
import { AuthProvider } from "../../context/AuthContext"
import { currentAuthenticatedUser, signOut } from "../../screens/auth/cognito"
import {
    APOLLO_CACHE_PREFIX,
    composeApolloPersistKey,
    hasSpecificResponseError,
    hookLocalStorageGetItem,
    hookLocalStorageSetItem,
} from "./utils"

let apolloClient = null

const getButterAuthToken = (cognitoUser: Maybe<any>) => {
    if (!cognitoUser) {
        return null
    }
    if (cognitoUser.attributes) {
        return `c/${cognitoUser.signInUserSession.idToken.jwtToken}`
    } else if (cognitoUser.provider === AuthProvider.Facebook) {
        return `f/${cognitoUser.accessToken}`
    } else if (cognitoUser.provider === AuthProvider.Google) {
        return `g/${cognitoUser.accessToken}`
    } else {
        return null
    }
}

async function create(initialState, router: RouterProps) {
    const httpLink = new HttpLink({
        uri: envVars.GRAPHQL_SERVER_URL,
    })
    const errorLink = onError((error) => {
        if (error.graphQLErrors) {
            error.graphQLErrors.forEach(({ message, locations, path }) => {
                logger.error(`[GraphQL Error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
            })
        }
        if (error.networkError) {
            logger.error(`[GraphQL Network error]: ${error.networkError}`)
        }

        // Check authentication validity
        if (hasSpecificResponseError(error, ResponseErrorType.InvalidAuth)) {
            signOut().then(() => {
                if (isBrowser) {
                    location.reload()
                }
            })
        }
    })
    const headersLink = setContext((_, { headers }) => {
        const account = getRouterQuery(router, "accountSlug")
        const location = getRouterQuery(router, "locationSlug")
        const tableId = getTableOrderingId(router)

        return currentAuthenticatedUser()
            .then((user) => {
                const token = getButterAuthToken(user)

                return {
                    headers: {
                        ...headers,
                        ...(account ? { "butter-account": account } : null),
                        ...(location ? { "butter-location": location } : null),
                        ...(token ? { "butter-auth-token": token } : null),
                        ...(tableId ? { "butter-table-id": tableId } : null),
                    },
                }
            })
            .catch((_) => {
                return {
                    headers: {
                        ...headers,
                        ...(account ? { "butter-account": account } : null),
                        ...(location ? { "butter-location": location } : null),
                        ...(tableId ? { "butter-table-id": tableId } : null),
                    },
                }
            })
    })

    const link = ApolloLink.from([errorLink, headersLink, httpLink])

    const cache = new InMemoryCache({}).restore(initialState || {})

    if (typeof window === "object") {
        hookLocalStorageSetItem()
        hookLocalStorageGetItem()
        const aSlug = getRouterQuery(router, "accountSlug") || ""
        const lSlug = getRouterQuery(router, "locationSlug") || ""
        try {
            persistCache({
                cache: cache,
                storage: window.localStorage,
                key: composeApolloPersistKey(APOLLO_CACHE_PREFIX, aSlug, lSlug),
                maxSize: false, // Don't limit the storage
            })
        } catch (error) {
            // If initialization of apollo-cache-persist fails,
            // we delete all the apollo-cache related data from localstorage.
            logger.warn("Could not initialize apollo-cache, clearing the apollo data from cache", error)
            Object.keys(window.localStorage).forEach((key) => {
                if (key.includes(APOLLO_CACHE_PREFIX)) {
                    localStorage.removeItem(key)
                }
            })
        }
    }

    // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
    return new ApolloClient({
        connectToDevTools: isBrowser,
        ssrMode: !isBrowser, // Disables forceFetch on the server (so queries are only run once)
        link: link,
        cache: cache,
    })
}

export default async function initApollo(router: RouterProps, initialState?: any): Promise<ApolloClient<any>> {
    // Make sure to create a new client for every server-side request so that data
    // isn't shared between connections (which would be bad)
    if (!isBrowser) {
        return await create(initialState, router)
    }

    // Reuse client on the client-side
    if (!apolloClient) {
        apolloClient = await create(initialState, router)
    }

    return apolloClient
}
